export type CompanyLogoLicenseStatus =
  | "review-required"
  | "reviewed"
  | "blocked";

export interface CompanyLogoEntry {
  instrumentId: string;
  ticker: string;
  entityName: string;
  relationship: "issuer" | "fund-sponsor" | "other";
  relationshipNote: string | null;
  providerSymbol: string;
  asset: {
    path: string;
    mimeType: string;
    byteSize: number;
    hash: { algorithm: "sha256"; value: string };
  };
  source: {
    provider: string;
  };
  license: {
    status: CompanyLogoLicenseStatus;
    name: string;
  };
  review?: {
    status: Exclude<CompanyLogoLicenseStatus, "blocked">;
    reviewedAt: string | null;
    scope: string | null;
    note: string;
    approvedSourceHash: { algorithm: "sha1"; value: string } | null;
    approvedAssetHash: { algorithm: "sha256"; value: string } | null;
  };
  fetchedAt: string;
  cachePolicy: {
    mode: "vendored";
    revalidateAfterDays: number;
  };
}

export interface CompanyLogoManifest {
  schemaVersion: 1;
  generatedAt: string | null;
  entries: Record<string, CompanyLogoEntry>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isValidEntry(value: unknown): value is CompanyLogoEntry {
  if (
    !isRecord(value) ||
    !isRecord(value.asset) ||
    !isRecord(value.source) ||
    !isRecord(value.license)
  ) {
    return false;
  }
  const hash = value.asset.hash;
  return (
    typeof value.instrumentId === "string" &&
    typeof value.ticker === "string" &&
    typeof value.asset.path === "string" &&
    value.asset.path.startsWith("/company-logos/") &&
    typeof value.asset.mimeType === "string" &&
    typeof value.asset.byteSize === "number" &&
    typeof value.source.provider === "string" &&
    isRecord(hash) &&
    hash.algorithm === "sha256" &&
    typeof hash.value === "string" &&
    /^[a-f0-9]{64}$/.test(hash.value) &&
    (value.license.status === "review-required" ||
      value.license.status === "reviewed" ||
      value.license.status === "blocked") &&
    typeof value.license.name === "string" &&
    typeof value.fetchedAt === "string"
  );
}

export function parseCompanyLogoManifest(
  value: unknown,
): CompanyLogoManifest {
  if (!isRecord(value) || value.schemaVersion !== 1 || !isRecord(value.entries)) {
    return { schemaVersion: 1, generatedAt: null, entries: {} };
  }

  const entries = Object.fromEntries(
    Object.entries(value.entries).filter(([, entry]) => isValidEntry(entry)),
  ) as Record<string, CompanyLogoEntry>;

  return {
    schemaVersion: 1,
    generatedAt:
      typeof value.generatedAt === "string" ? value.generatedAt : null,
    entries,
  };
}

export function resolveCompanyLogo(
  manifest: CompanyLogoManifest,
  query: { instrumentId?: string; ticker?: string },
  options: { allowUnreviewed?: boolean } = {},
): CompanyLogoEntry | null {
  const byInstrument = query.instrumentId
    ? manifest.entries[query.instrumentId]
    : undefined;
  const normalizedTicker = query.ticker?.trim().toUpperCase();
  const entry =
    byInstrument ??
    (normalizedTicker
      ? Object.values(manifest.entries).find(
          ({ ticker }) => ticker === normalizedTicker,
        )
      : undefined);

  if (!entry || entry.license.status === "blocked") return null;
  if (
    entry.license.status !== "reviewed" &&
    options.allowUnreviewed !== true
  ) {
    return null;
  }
  return entry;
}
